// src/components/extraction/RechnungFieldsView.jsx
// Editierbare Ansicht der extrahierten Rechnungsfelder. Ruft bei jeder Eingabe
// onRechnungChange(neueRechnung) mit einer neuen, unveränderten Kopie des Rechnung-Objekts auf -
// der Aufrufer (Extraction.jsx) entscheidet, ob das in die laufende Neu-Erfassung oder in einen
// bestehenden Listen-Eintrag geschrieben wird (siehe useRechnungListe.aktualisieren).
// "empfaenger"/"empfaenger_adresse" werden nicht angezeigt. "positionen" bleibt read-only,
// dargestellt von PositionenView unterhalb dieses Grids.
//
// Kritische Validierungsfehler und Warnungen (aus validation.validationJson) werden weiterhin
// auf dem jeweiligen Feld farblich markiert (rot = kritisch, gelb = Warnung) - bezogen auf die
// letzte bekannte Validierung, nicht live neu berechnet.
//
// Layout als CSS-Grid (2 Spalten: Label/Eingabe) statt Tabelle - die Gesamtbreite lässt sich
// zentral über .rechnung-felder-grid { max-width: ... } in App.css einstellen.
import React from 'react'
import getMwstSpalten from './mwstSpalten'
import CurrencyInput from '../../components/shared/CurrencyInput'
import PositionenView from './PositionenView'
import { istIbanAuswahlOffen } from './rechnungAnsicht'

const TEXT_FIELDS = [
  { key: 'lieferant', label: 'Lieferant' },
  { key: 'lieferant_adresse', label: 'Lieferant-Adresse' },
  { key: 'umsatzsteuer_id', label: 'Umsatzsteuer-ID' },
  { key: 'rechnungsnummer', label: 'Rechnungsnummer' }
]

// Bildet die FeldName-Werte aus RechnungValidierung.cs (C#-Property-Namen, teils mit
// Sonderfällen wie "Bruttobetrag" statt "BruttobetragEur" bei der Plausibilitätsprüfung)
// auf unsere Anzeige-Keys (snake_case) ab.
const FIELD_NAME_ALIASES = {
  Rechnungsnummer: 'rechnungsnummer',
  Rechnungsdatum: 'rechnungsdatum',
  Lieferant: 'lieferant',
  LieferantAdresse: 'lieferant_adresse',
  BruttobetragEur: 'bruttobetrag_eur',
  Bruttobetrag: 'bruttobetrag_eur',
  NettobetragEur: 'nettobetrag_eur',
  MwstBetragEur: 'mwst_betrag_eur',
  Skonto: 'skonto',
  UmsatzsteuerId: 'umsatzsteuer_id'
}

function parseValidationJson (validationJson) {
  if (!validationJson) return null
  try {
    return JSON.parse(validationJson)
  } catch {
    return null
  }
}

function buildFieldIssueMap (eintraege) {
  const map = {}
  ;(eintraege || []).forEach(eintrag => {
    const displayKey = FIELD_NAME_ALIASES[eintrag.feld]
    if (displayKey) map[displayKey] = eintrag
  })
  return map
}

function FeldZeile ({ label, rowClass, hinweis, icon, children }) {
  const klasse = ['feld-label', rowClass].filter(Boolean).join(' ')
  const klasseWert = ['feld-wert', rowClass].filter(Boolean).join(' ')
  return (
    <>
      <div className={klasse}>{label}</div>
      <div className={klasseWert}>
        {children}
        {icon && (
          <span className='issue-icon' title={hinweis}>
            {' '}
            {icon}
          </span>
        )}
      </div>
    </>
  )
}

function RechnungFieldsView ({ rechnung, validation, onRechnungChange }) {
  if (!rechnung) return null

  const parsed = parseValidationJson(validation?.validationJson)
  const criticalFields = buildFieldIssueMap(parsed?.kritischeFehler)
  const warningFields = buildFieldIssueMap(parsed?.warnungen)
  const wurdeKompensiert = parsed?.kompensiert === true

  // Alle Meldungen aus der Validierung, unabhängig davon, ob sie sich einem bekannten Feld
  // zuordnen lassen (z.B. "Summe der Positionen stimmt weder mit Netto- noch mit Bruttobetrag
  // überein" hängt am Feld "Positionen", das hier nicht einzeln angezeigt wird - siehe
  // PositionenView weiter unten). Damit der Benutzer hier dieselben Warnungen sieht wie später
  // in der Tabelle (rechnungAnsicht.js: ermittleKommentar), werden ALLE Meldungen als Banner
  // ausgegeben - zusätzlich zur farblichen Feld-Markierung, die nur bei bekannten Feldern greift.
  const kritischeMeldungen = (parsed?.kritischeFehler ?? []).map(e => e.meldung)
  const warnMeldungen = (parsed?.warnungen ?? []).map(e => e.meldung)
  // Fallback, wenn gar kein strukturiertes validationJson vorliegt (z.B. Fehlerzeile aus der
  // Stapelverarbeitung: validation = { valid: false, remarks: 'Fehler bei der Extraktion: ...' }).
  const fallbackMeldung =
    !parsed && validation?.valid === false ? validation?.remarks : null

  const issueRow = key => {
    const critical = criticalFields[key]
    const warning = !critical ? warningFields[key] : undefined
    return {
      rowClass: critical
        ? 'field-critical'
        : warning
        ? 'field-warning'
        : undefined,
      hinweis: critical?.meldung || warning?.meldung,
      icon: critical ? '⚠' : warning ? '!' : null
    }
  }

  const setFeld = (key, value) => {
    onRechnungChange?.({ ...rechnung, [key]: value })
  }

  const setMwstFeld = (satz, value) => {
    onRechnungChange?.({
      ...rechnung,
      mwst_betrag_eur: { ...(rechnung.mwst_betrag_eur || {}), [satz]: value }
    })
  }

  // Bewusst EIN gemeinsamer Setter für beide Felder (nicht zweimal setFeld hintereinander) -
  // sonst würde der erste Aufruf mit dem noch alten "rechnung" aus dem Closure überschrieben,
  // sobald der zweite Aufruf ebenfalls von {...rechnung, ...} ausgeht.
  const handleIbanAuswahl = iban => {
    onRechnungChange?.({
      ...rechnung,
      ausgewaehlte_iban: iban,
      iban_manuell_gewaehlt: true
    })
  }

  // Bestätigt die bereits vorausgewählte (erste) IBAN, OHNE ihren Wert zu ändern - wichtig für
  // den Fall, dass genau diese erste IBAN schon die richtige ist: ein <select> feuert onChange
  // nur bei einer tatsächlichen Wertänderung, ein erneutes Auswählen derselben Option löst also
  // NICHTS aus. Diese Checkbox ist der einzige Weg, die Vorauswahl explizit zu bestätigen, ohne
  // erst auf eine andere IBAN und wieder zurück wechseln zu müssen.
  const handleIbanBestaetigen = () => {
    onRechnungChange?.({
      ...rechnung,
      ausgewaehlte_iban:
        rechnung.ausgewaehlte_iban ??
        rechnung.bankverbindungen_iban?.[0]?.iban ??
        null,
      iban_manuell_gewaehlt: true
    })
  }

  return (
    <div className='rechnung-result'>
      {kritischeMeldungen.length > 0 && (
        <div className='error-banner'>
          {kritischeMeldungen.map((meldung, i) => (
            <div key={`kritisch-${i}`}>⚠ {meldung}</div>
          ))}
        </div>
      )}

      {warnMeldungen.length > 0 && (
        <div className='info-banner'>
          {warnMeldungen.map((meldung, i) => (
            <div key={`warnung-${i}`}>! {meldung}</div>
          ))}
        </div>
      )}

      {fallbackMeldung && (
        <div className='error-banner'>⚠ {fallbackMeldung}</div>
      )}

      {wurdeKompensiert && (
        <div className='info-banner'>
          Hinweis: Brutto- und Nettobetrag stimmen rechnerisch nicht exakt
          überein, die Summe der Rechnungspositionen bestätigt aber den
          Bruttobetrag - daher kein kritischer Fehler.
        </div>
      )}

      <div className='rechnung-felder-grid'>
        {TEXT_FIELDS.map(({ key, label }) => {
          const { rowClass, hinweis, icon } = issueRow(key)
          return (
            <FeldZeile
              key={key}
              label={label}
              rowClass={rowClass}
              hinweis={hinweis}
              icon={icon}
            >
              <input
                type='text'
                value={rechnung[key] ?? ''}
                onChange={e => setFeld(key, e.target.value || null)}
              />
            </FeldZeile>
          )
        })}

        {(() => {
          const { rowClass, hinweis, icon } = issueRow('rechnungsdatum')
          return (
            <FeldZeile
              label='Rechnungsdatum'
              rowClass={rowClass}
              hinweis={hinweis}
              icon={icon}
            >
              <input
                type='date'
                value={rechnung.rechnungsdatum ?? ''}
                onChange={e =>
                  setFeld('rechnungsdatum', e.target.value || null)
                }
              />
            </FeldZeile>
          )
        })()}

        {(() => {
          const { rowClass, hinweis, icon } = issueRow('nettobetrag_eur')
          return (
            <FeldZeile
              label='Nettobetrag (EUR)'
              rowClass={rowClass}
              hinweis={hinweis}
              icon={icon}
            >
              <CurrencyInput
                value={rechnung.nettobetrag_eur}
                onChange={num => setFeld('nettobetrag_eur', num)}
              />
            </FeldZeile>
          )
        })()}

        {(() => {
          const { rowClass, hinweis, icon } = issueRow('skonto')
          return (
            <FeldZeile
              label='Skonto (EUR)'
              rowClass={rowClass}
              hinweis={hinweis}
              icon={icon}
            >
              <CurrencyInput
                value={rechnung.skonto}
                onChange={num => setFeld('skonto', num)}
              />
            </FeldZeile>
          )
        })()}

        {getMwstSpalten().map(({ key: satz, label }) => (
          <FeldZeile key={satz} label={label}>
            <CurrencyInput
              value={rechnung.mwst_betrag_eur?.[satz]}
              onChange={num => setMwstFeld(satz, num)}
            />
          </FeldZeile>
        ))}

        {(() => {
          const { rowClass, hinweis, icon } = issueRow('bruttobetrag_eur')
          return (
            <FeldZeile
              label='Bruttobetrag (EUR)'
              rowClass={rowClass}
              hinweis={hinweis}
              icon={icon}
            >
              <CurrencyInput
                value={rechnung.bruttobetrag_eur}
                onChange={num => setFeld('bruttobetrag_eur', num)}
              />
            </FeldZeile>
          )
        })()}

        {Array.isArray(rechnung.bankverbindungen_iban) &&
          rechnung.bankverbindungen_iban.length > 0 &&
          (() => {
            const auswahlOffen = istIbanAuswahlOffen(rechnung)
            const ausgewaehlt =
              rechnung.ausgewaehlte_iban ??
              rechnung.bankverbindungen_iban[0]?.iban ??
              ''
            return (
              <FeldZeile
                label='IBAN'
                rowClass={auswahlOffen ? 'field-warning' : undefined}
                hinweis={
                  auswahlOffen
                    ? 'Es liegen mehrere Bankverbindungen vor - bitte die richtige IBAN auswählen.'
                    : undefined
                }
                icon={auswahlOffen ? '!' : null}
              >
                {rechnung.bankverbindungen_iban.length === 1 ? (
                  <input type='text' value={ausgewaehlt} disabled />
                ) : (
                  <div className='iban-auswahl-gruppe'>
                    <select
                      value={ausgewaehlt}
                      onChange={e => handleIbanAuswahl(e.target.value)}
                    >
                      {rechnung.bankverbindungen_iban.map((bv, i) => (
                        <option key={i} value={bv.iban ?? ''}>
                          {bv.iban}
                        </option>
                      ))}
                    </select>
                    {auswahlOffen && (
                      <label className='iban-bestaetigen'>
                        <input
                          type='checkbox'
                          checked={false}
                          onChange={handleIbanBestaetigen}
                        />
                        angezeigte IBAN korrekt
                      </label>
                    )}
                  </div>
                )}
              </FeldZeile>
            )
          })()}

        <FeldZeile label='Kommentar'>
          <textarea
            className='kommentar-eingabe'
            value={rechnung.kommentar ?? ''}
            onChange={e => setFeld('kommentar', e.target.value)}
          />
        </FeldZeile>
      </div>

      <PositionenView positionen={rechnung.positionen} />
    </div>
  )
}

export default RechnungFieldsView
